import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, ApiError } from '../lib/api';
import type { AdminBundleSummary } from '../../shared/types';
import Eyebrow from './Eyebrow';
import Button from './Button';
import RowMenu from './RowMenu';
import ConfirmModal from './ConfirmModal';
import { timeAgo } from '../lib/timeAgo';

type SubView = 'active' | 'archived';

/**
 * Content for the dashboard's "Links" tab (AdminDashboard.tsx). Bundles are
 * a separate collection from scripts, so this owns its own fetch — the
 * parent only hands down the shared search box's current query.
 *
 * Active/archived is its own sub-toggle rather than reusing the dashboard's
 * top-level Archived tab: GET /api/admin/bundles only ever returns one view
 * (?view=active|archived) at a time, same split as listScripts, so mirroring
 * that split here keeps one bundle fetch = one clear list, with archived
 * bundles still reachable and unarchivable from inside the Links tab itself.
 */
export default function BundlesView({ search }: { search: string }) {
  const navigate = useNavigate();
  const [subView, setSubView] = useState<SubView>('active');
  const [bundles, setBundles] = useState<AdminBundleSummary[] | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<AdminBundleSummary | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  function refresh() {
    setBundles(null);
    adminApi.listBundles({ view: subView }).then((res) => setBundles(res.bundles));
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subView]);

  const filtered = useMemo(() => {
    if (!bundles) return null;
    const q = search.trim().toLowerCase();
    if (!q) return bundles;
    return bundles.filter(
      (b) =>
        b.title.toLowerCase().includes(q) ||
        (b.client_name ?? '').toLowerCase().includes(q),
    );
  }, [bundles, search]);

  async function handleArchive(bundle: AdminBundleSummary, archived: boolean) {
    setActionError(null);
    try {
      await adminApi.archiveBundle(bundle.id, archived);
      refresh();
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : 'Failed to update this link. Try again.',
      );
    }
  }

  async function copyLink(bundle: AdminBundleSummary) {
    const url = `${window.location.origin}/b/${bundle.slug}`;
    await navigator.clipboard.writeText(url).catch(() => {});
    setCopiedId(bundle.id);
    setTimeout(() => setCopiedId((cur) => (cur === bundle.id ? null : cur)), 1500);
  }

  async function confirmDelete() {
    if (!deleteCandidate) return;
    await adminApi.deleteBundle(deleteCandidate.id);
    setDeleteCandidate(null);
    refresh();
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <SubTabs value={subView} onChange={setSubView} />
      </div>

      {actionError && (
        <div className="bg-accent/10 border border-accent/40 text-accent text-[13px] px-4 py-3 rounded-md mb-4">
          {actionError}
        </div>
      )}

      {bundles === null ? (
        <SkeletonList />
      ) : bundles.length === 0 ? (
        subView === 'archived' ? (
          <ArchivedEmptyState />
        ) : (
          <EmptyState onCreate={() => navigate('/admin/links/new')} />
        )
      ) : filtered && filtered.length === 0 ? (
        <SearchEmptyState query={search} />
      ) : (
        <ul className="space-y-2">
          {(filtered ?? bundles).map((bundle, i) => (
            <li
              key={bundle.id}
              className="group animate-rise-subtle"
              style={{ animationDelay: `${Math.min(i, 6) * 60}ms` }}
            >
              <BundleRow
                bundle={bundle}
                archivedView={subView === 'archived'}
                copied={copiedId === bundle.id}
                onCopyLink={() => copyLink(bundle)}
                onEdit={() => navigate(`/admin/links/${bundle.id}`)}
                onArchive={(archived) => handleArchive(bundle, archived)}
                onDelete={() => setDeleteCandidate(bundle)}
              />
            </li>
          ))}
        </ul>
      )}

      {deleteCandidate && (
        <ConfirmModal
          eyebrow="Delete link"
          title="Delete this link forever?"
          body={
            <>
              <p className="mb-2">
                <span className="text-fg font-medium">{deleteCandidate.title}</span>
                {deleteCandidate.client_name && (
                  <>
                    <span className="text-faint"> · </span>
                    {deleteCandidate.client_name}
                  </>
                )}
              </p>
              <p>
                This removes the link and its item list. The scripts inside it
                are never touched — only the bundle. This can't be undone.
              </p>
            </>
          }
          confirmLabel="Delete forever"
          busyLabel="Deleting…"
          onCancel={() => setDeleteCandidate(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

// ---------- Sub-tabs (Active / Archived) ----------

function SubTabs({ value, onChange }: { value: SubView; onChange: (v: SubView) => void }) {
  return (
    <div className="inline-flex rounded-full bg-elevated border border-line p-0.5">
      {(
        [
          { id: 'active', label: 'Active' },
          { id: 'archived', label: 'Archived' },
        ] as Array<{ id: SubView; label: string }>
      ).map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`px-3.5 py-1.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] font-medium transition-colors duration-200 ${
            value === tab.id ? 'bg-fg text-page' : 'text-muted hover:text-fg'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ---------- Row ----------

function BundleRow({
  bundle,
  archivedView,
  copied,
  onCopyLink,
  onEdit,
  onArchive,
  onDelete,
}: {
  bundle: AdminBundleSummary;
  archivedView: boolean;
  copied: boolean;
  onCopyLink: () => void;
  onEdit: () => void;
  onArchive: (archived: boolean) => void;
  onDelete: () => void;
}) {
  return (
    <div
      className="
        relative flex items-center justify-between gap-6
        bg-card border border-line rounded-md px-6 py-5
        transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
        group-hover:border-accent
      "
    >
      <div className="min-w-0 flex-1">
        <div className="font-display font-semibold text-[18px] text-fg truncate">
          {bundle.title}
        </div>
        <div className="mt-1 flex items-center gap-2 text-[13px] text-muted flex-wrap">
          {bundle.client_name && (
            <>
              <span>{bundle.client_name}</span>
              <Dot />
            </>
          )}
          <span>
            {bundle.item_count} script{bundle.item_count === 1 ? '' : 's'}
          </span>
          <Dot />
          {archivedView ? (
            <span className="text-faint">
              Archived {bundle.archived_at ? formatShortDate(bundle.archived_at) : ''}
            </span>
          ) : (
            <>
              <span>{approvalSummary(bundle)}</span>
              <Dot />
              <span className="text-faint">Updated {timeAgo(bundle.updated_at)}</span>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-none">
        <KindPill kind={bundle.kind} />
        {archivedView ? (
          <>
            <Button variant="secondary" size="sm" onClick={onEdit}>
              Edit
            </Button>
            <Button variant="secondary" size="sm" onClick={() => onArchive(false)}>
              Unarchive
            </Button>
            <RowMenu items={[{ label: 'Delete', onClick: onDelete, destructive: true }]} />
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onCopyLink}
              title="Copy link"
              aria-label="Copy link"
              className="
                w-8 h-8 rounded-full border border-line
                flex items-center justify-center text-muted text-[13px]
                hover:text-accent hover:border-accent transition-colors duration-200
              "
            >
              {copied ? '✓' : '⧉'}
            </button>
            <Button variant="secondary" size="sm" onClick={onEdit}>
              Edit
            </Button>
            <RowMenu
              items={[
                { label: 'Archive', onClick: () => onArchive(true) },
                { label: 'Delete', onClick: onDelete, destructive: true },
              ]}
            />
          </>
        )}
      </div>
    </div>
  );
}

function KindPill({ kind }: { kind: AdminBundleSummary['kind'] }) {
  if (kind === 'shoot') {
    return (
      <span className="shrink-0 font-mono text-[10px] uppercase font-medium tracking-[0.12em] px-2.5 py-1 rounded-full border border-gold/40 bg-gold/10 text-gold">
        Shoot pack
      </span>
    );
  }
  return (
    <span className="shrink-0 font-mono text-[10px] uppercase font-medium tracking-[0.12em] px-2.5 py-1 rounded-full border border-accent/40 bg-accent/10 text-accent">
      Review
    </span>
  );
}

function approvalSummary(bundle: AdminBundleSummary): string {
  if (bundle.kind === 'shoot') return 'Read-only pack';
  if (bundle.ready_count === 0) return 'Nothing ready yet';
  return `${bundle.approved_count} of ${bundle.ready_count} approved`;
}

function formatShortDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function Dot() {
  return <span className="text-faint/60" aria-hidden>·</span>;
}

// ---------- Empty / loading states ----------

function SkeletonList() {
  return (
    <ul className="space-y-2">
      {[0, 1, 2].map((i) => (
        <li key={i} className="h-[80px] bg-card border border-line rounded-md skeleton" />
      ))}
    </ul>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="border border-dashed border-line rounded-md px-12 py-16 md:py-24 text-center bg-card">
      <Eyebrow>// Empty</Eyebrow>
      <h2 className="font-display font-bold text-[32px] md:text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-3">
        No links yet
      </h2>
      <p className="text-muted max-w-md mx-auto mb-8">
        Bundle a curated set of scripts behind one shareable link — a review
        batch, a staged delivery, or a read-only shoot-day pack.
      </p>
      <Button variant="primary" glow onClick={onCreate}>
        + New link
      </Button>
    </div>
  );
}

function ArchivedEmptyState() {
  return (
    <div className="border border-dashed border-line rounded-md px-12 py-16 md:py-24 text-center bg-card">
      <Eyebrow>// Archive</Eyebrow>
      <h2 className="font-display font-bold text-[32px] md:text-[40px] leading-[1.05] tracking-[-0.02em] mt-3 mb-3">
        Nothing archived
      </h2>
      <p className="text-muted max-w-md mx-auto">
        Archive a link from its row menu once it's no longer needed — the URL
        stops resolving for clients until you unarchive it.
      </p>
    </div>
  );
}

function SearchEmptyState({ query }: { query: string }) {
  return (
    <div className="border border-dashed border-line rounded-md px-12 py-16 text-center bg-card">
      <Eyebrow>// No matches</Eyebrow>
      <h2 className="font-display font-bold text-[28px] leading-[1.1] tracking-[-0.02em] mt-3 mb-3">
        Nothing matches "{query}"
      </h2>
      <p className="text-muted max-w-md mx-auto">
        We searched link titles and client names. Try a different term.
      </p>
    </div>
  );
}
